/**
 * creature/renderer.ts — Drawing Living Things
 *
 * Procedural Canvas 2D rendering from a solved skeleton. Every creature
 * is drawn each frame from joints and shapes — no sprite sheets, no
 * external assets. This is the generalized form of title.ts _drawCat.
 *
 * The renderer walks the SkeletonDef's drawOrder groups and draws
 * body parts using the species' bodyShape and palette. It handles:
 * - 8-direction isometric facing (mirror for N/NW/NE, foreshorten for E/W)
 * - Procedural details: ears, eyes, nose, whiskers, tail, mane
 * - Fire glow overlay
 * - Quality-level degradation
 */

import type { SpeciesTemplate, BodyShape, CreaturePalette } from "./species.js";
import type { CreaturePose } from "./pose.js";

// ─── Render Context ─────────────────────────────────────────────────────────

/** Everything the renderer needs for one creature draw call */
export interface CreatureRenderContext {
  /** Canvas 2D context */
  ctx: CanvasRenderingContext2D;
  /** Species template (body shape, palette, skeleton def) */
  species: SpeciesTemplate;
  /** Current interpolated pose */
  pose: CreaturePose;
  /** Screen-space position of the hip (root joint) */
  rootPos: { x: number; y: number };
  /** Render scale (pixels per world unit) */
  scale: number;
  /** Facing: 1 = right/south, -1 = left/north (mirror) */
  facing: number;
  /** Current time in seconds (for procedural animations) */
  time: number;
  /** Quality level 0-1 (1 = full detail, 0.5 = reduced) */
  quality: number;
  /** Whether the creature is currently walking */
  isWalking: boolean;
  /** Optional: ambient light color for fire glow */
  ambientGlow?: string;
  /** Player tile position for creature eye tracking */
  playerTileX?: number;
  playerTileY?: number;
  /** Creature's tile position (for computing gaze direction) */
  creatureTileX?: number;
  creatureTileY?: number;
  /** Unique creature ID (for blink phase offset) */
  creatureId?: string;
  /** Nearby creature positions for social gaze */
  nearbyCreatures?: Array<{ tileX: number; tileY: number }>;
}

// ─── Creature Attention State ──────────────────────────────────────────────
// Per-creature attention: smooth gaze interpolation, multi-phase blink,
// idle micro-saccades, and social gaze toward nearby creatures.
// This makes creatures feel truly alive — not just animated, but aware.

interface CreatureAttention {
  // ── Gaze ──
  gazeX: number;          // current interpolated gaze X offset
  gazeY: number;          // current interpolated gaze Y offset
  targetGazeX: number;    // where gaze is heading
  targetGazeY: number;
  // ── Blink ──
  blinkTimer: number;     // countdown to next blink (seconds)
  blinkPhase: number;     // 0-1 through current blink (0 = fully open)
  blinkDuration: number;  // how long this blink takes
  isBlinking: boolean;
  // ── Saccade ──
  saccadeTimer: number;   // countdown to next micro-saccade
  saccadeX: number;       // current saccade target
  saccadeY: number;
  // ── Social ──
  socialGazeTimer: number;  // countdown to next social glance
  socialTargetX: number;
  socialTargetY: number;
  isSocialGazing: boolean;
  // ── Timing ──
  prevTime: number;         // previous frame time for dt computation
}

function createCreatureAttention(seed: number): CreatureAttention {
  // Stagger initial timers by seed so creatures don't blink/saccade in unison
  const seedF = (seed % 97) / 97;  // pseudo-random 0-1 from creature ID
  return {
    gazeX: 0, gazeY: 0, targetGazeX: 0, targetGazeY: 0,
    blinkTimer: 2 + seedF * 4,     // first blink 2-6s after spawn
    blinkPhase: 0, blinkDuration: 0.35, isBlinking: false,
    saccadeTimer: 0.5 + seedF * 2, saccadeX: 0, saccadeY: 0,
    socialGazeTimer: 3 + seedF * 5,
    socialTargetX: 0, socialTargetY: 0, isSocialGazing: false,
    prevTime: 0,
  };
}

/** Per-creature attention state map — lazily created, persists across frames */
const creatureAttentionMap = new Map<string, CreatureAttention>();

/** Blink timing constants */
const C_BLINK_MIN = 2.5;     // minimum seconds between blinks
const C_BLINK_MAX = 7.0;     // maximum — creatures blink slower than humans
const C_BLINK_DUR_MIN = 0.25;
const C_BLINK_DUR_MAX = 0.45;
/** Saccade constants */
const C_SACC_MIN = 1.0;
const C_SACC_MAX = 4.0;
const C_SACC_AMP = 0.6;      // smaller than human — subtle eye darts
/** Gaze interpolation */
const C_GAZE_LERP = 5;       // slightly slower than human — contemplative
/** Social gaze */
const C_SOCIAL_MIN = 4.0;
const C_SOCIAL_MAX = 10.0;
const C_SOCIAL_HOLD = 1.5;   // how long a social glance holds

function clampC(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function lerpC(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(t, 1);
}

/**
 * Update creature attention state and return computed eye offsets + blink phase.
 * This is the creature equivalent of character/attention.ts updateAttention().
 */
function updateCreatureAttention(
  rc: CreatureRenderContext,
  s: number
): { eyeOffX: number; eyeOffY: number; blinkPhase: number } {
  const id = rc.creatureId ?? "unknown";
  let att = creatureAttentionMap.get(id);
  if (!att) {
    const seed = id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    att = createCreatureAttention(seed);
    att.prevTime = rc.time;
    creatureAttentionMap.set(id, att);
  }

  // Compute dt from time delta (capped to prevent physics explosion on tab-back)
  const rawDt = rc.time - att.prevTime;
  const dt = Math.min(Math.max(rawDt, 0), 0.1);
  att.prevTime = rc.time;

  // ── 1. Determine gaze target ────────────────────────────────────────────

  // Priority: social gaze < player gaze < saccade idle
  att.targetGazeX = 0;
  att.targetGazeY = 0;
  let hasTarget = false;

  // Player gaze — creatures look toward nearby player
  if (rc.playerTileX !== undefined && rc.playerTileY !== undefined &&
      rc.creatureTileX !== undefined && rc.creatureTileY !== undefined) {
    const pdx = rc.playerTileX - rc.creatureTileX;
    const pdy = rc.playerTileY - rc.creatureTileY;
    const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
    if (pdist < 6) {
      const strength = Math.min(1, (6 - pdist) / 4);
      att.targetGazeX = (pdx / (pdist || 1)) * strength * 0.8 * s;
      att.targetGazeY = (pdy / (pdist || 1)) * strength * 0.3 * s;
      hasTarget = true;
    }
  }

  // Social gaze — occasional glance at nearby creatures
  att.socialGazeTimer -= dt;
  if (att.socialGazeTimer <= 0 && !att.isSocialGazing) {
    const nearby = rc.nearbyCreatures;
    if (nearby && nearby.length > 0 && rc.creatureTileX !== undefined && rc.creatureTileY !== undefined) {
      // Pick a random nearby creature to glance at
      const target = nearby[Math.floor(Math.random() * nearby.length)];
      const sdx = target.tileX - rc.creatureTileX;
      const sdy = target.tileY - rc.creatureTileY;
      const sdist = Math.sqrt(sdx * sdx + sdy * sdy);
      if (sdist > 0 && sdist < 5) {
        att.socialTargetX = (sdx / sdist) * 0.6 * s;
        att.socialTargetY = (sdy / sdist) * 0.2 * s;
        att.isSocialGazing = true;
        att.socialGazeTimer = C_SOCIAL_HOLD;
      } else {
        att.socialGazeTimer = C_SOCIAL_MIN + Math.random() * (C_SOCIAL_MAX - C_SOCIAL_MIN);
      }
    } else {
      att.socialGazeTimer = C_SOCIAL_MIN + Math.random() * (C_SOCIAL_MAX - C_SOCIAL_MIN);
    }
  } else if (att.isSocialGazing && att.socialGazeTimer <= 0) {
    // Social glance finished
    att.isSocialGazing = false;
    att.socialGazeTimer = C_SOCIAL_MIN + Math.random() * (C_SOCIAL_MAX - C_SOCIAL_MIN);
  }

  // Social gaze overrides idle saccade but not player gaze
  if (!hasTarget && att.isSocialGazing) {
    att.targetGazeX = att.socialTargetX;
    att.targetGazeY = att.socialTargetY;
    hasTarget = true;
  }

  // Idle saccades — tiny random eye movements when nothing else draws attention
  if (!hasTarget) {
    att.saccadeTimer -= dt;
    if (att.saccadeTimer <= 0) {
      att.saccadeX = (Math.random() - 0.5) * C_SACC_AMP * s;
      att.saccadeY = (Math.random() - 0.5) * C_SACC_AMP * 0.4 * s;
      att.saccadeTimer = C_SACC_MIN + Math.random() * (C_SACC_MAX - C_SACC_MIN);
    }
    att.targetGazeX = att.saccadeX;
    att.targetGazeY = att.saccadeY;
  }

  // ── 2. Interpolate gaze (smooth, contemplative) ─────────────────────────
  att.gazeX = lerpC(att.gazeX, att.targetGazeX, C_GAZE_LERP * dt);
  att.gazeY = lerpC(att.gazeY, att.targetGazeY, C_GAZE_LERP * dt);

  // ── 3. Blink simulation (multi-phase, asymmetric) ───────────────────────
  if (!att.isBlinking) {
    att.blinkTimer -= dt;
    if (att.blinkTimer <= 0) {
      att.isBlinking = true;
      att.blinkPhase = 0;
      att.blinkDuration = C_BLINK_DUR_MIN + Math.random() * (C_BLINK_DUR_MAX - C_BLINK_DUR_MIN);
    }
  } else {
    att.blinkPhase += dt / att.blinkDuration;
    if (att.blinkPhase >= 1.0) {
      att.isBlinking = false;
      att.blinkPhase = 0;
      att.blinkTimer = C_BLINK_MIN + Math.random() * (C_BLINK_MAX - C_BLINK_MIN);
    }
  }

  return {
    eyeOffX: att.gazeX,
    eyeOffY: att.gazeY,
    blinkPhase: att.isBlinking ? att.blinkPhase : 0,
  };
}

/** Remove attention state for a creature (call when creature despawns) */
export function cleanupCreatureAttention(creatureId: string): void {
  creatureAttentionMap.delete(creatureId);
}

// ─── Main Render Function ───────────────────────────────────────────────────

/**
 * Render a creature procedurally from its skeleton and species data.
 * This is the main entry point — replaces title.ts _drawCat for any species.
 */
export function renderCreature(rc: CreatureRenderContext): void {
  const { ctx, species, pose, scale: s, facing: f, time: t } = rc;
  const bs = species.bodyShape;
  const pal = species.palette;

  // ── Compute skeleton joint positions (simplified FK inline) ──
  // This mirrors the title.ts approach: hip -> spine -> shoulder -> neck -> head
  const hip = { x: rc.rootPos.x, y: rc.rootPos.y };

  // Breathing
  const breatheAmp = pose.eyeOpen > 0.5 ? 1.0 : 1.8;
  const breatheFreq = pose.eyeOpen > 0.5 ? 1.4 : 0.85;
  const breathe = Math.sin(t * breatheFreq + pose.breathePhase) * breatheAmp * s * 0.1;

  // Spine angle: 0=flat, 1=upright
  const spineLen = bs.bodyLength * s;
  const spineAng = -0.45 - pose.spineAngle * 0.9;
  const shoulder = {
    x: hip.x + Math.cos(spineAng) * spineLen * f,
    y: hip.y + Math.sin(spineAng) * spineLen + breathe,
  };

  // Neck
  const neckLen = bs.neckLength * 5 * s;
  const neckAng = spineAng - 0.3 - pose.headLift * 0.6;
  const neckTop = {
    x: shoulder.x + Math.cos(neckAng) * neckLen * f,
    y: shoulder.y + Math.sin(neckAng) * neckLen,
  };

  // Head
  const head = {
    x: neckTop.x + pose.headTurn * 2 * s * f,
    y: neckTop.y,
  };
  const headR = bs.headRadius * s;

  // Floor level
  const floorY = hip.y + 4 * s * bs.legLength;

  // ── Ground shadow ──
  drawGroundShadow(ctx, hip, floorY, spineLen, s, f, pose);

  // ── Draw order: back wings -> back legs -> tail -> body -> front legs -> front wings -> head ──
  if (bs.hasWings) {
    drawWing(ctx, shoulder, s, f, t, bs, pal, -1, rc.quality); // back wing
  }
  drawBackLegs(ctx, hip, floorY, pose, s, f, bs, pal, rc.isWalking);
  drawTail(ctx, hip, pose, s, f, t, bs, pal);
  if (pose.scratchLeg > 0.1) {
    drawScratchLeg(ctx, hip, head, headR, pose, s, f, t, pal);
  }
  drawBody(ctx, hip, shoulder, spineLen, spineAng, pose, s, f, bs, pal);
  drawMarkings(ctx, hip, shoulder, spineLen, spineAng, s, f, bs, pal);
  drawFrontLegs(ctx, shoulder, floorY, pose, s, f, bs, pal, rc.isWalking);
  if (bs.hasWings) {
    drawWing(ctx, shoulder, s, f, t, bs, pal, 1, rc.quality); // front wing
  }
  // ── Creature attention: smooth gaze, multi-phase blink, saccades ──
  const att = updateCreatureAttention(rc, s);

  drawHead(ctx, head, headR, neckTop, pose, s, f, t, bs, pal, rc.quality, att.eyeOffX, att.eyeOffY, att.blinkPhase);
  if (bs.hasMane) {
    drawMane(ctx, shoulder, neckTop, head, spineAng, s, f, pal);
  }
  drawHorns(ctx, head, headR, s, f, bs, pal);
  drawFireGlow(ctx, hip, shoulder, spineLen, spineAng, pose, s, f, t, bs, rc.ambientGlow);

  // Reset canvas state — prevent lineWidth/alpha leaks into next entity render
  ctx.lineWidth = 1;
  ctx.globalAlpha = 1;
}

// ─── Back Legs ──────────────────────────────────────────────────────────────

function drawBackLegs(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  floorY: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette,
  isWalking: boolean
): void {
  if (pose.spineAngle <= 0.15) return;

  const vis = Math.min(1, (pose.spineAngle - 0.15) / 0.3);
  ctx.globalAlpha = vis;
  ctx.fillStyle = darken(pal.body, 0.15);

  const legW = bs.legThickness * s;
  const blStep = isWalking ? Math.sin(pose.walkPhase + Math.PI * 0.5) * 2 * s : 0;
  const brStep = isWalking ? Math.sin(pose.walkPhase + Math.PI * 1.5) * 2 * s : 0;

  // Left back
  ctx.beginPath();
  ctx.moveTo(hip.x + 1 * s * f, hip.y);
  ctx.lineTo(hip.x + (0 * s + blStep) * f, floorY);
  ctx.lineTo(hip.x + (legW + blStep) * f, floorY);
  ctx.lineTo(hip.x + (1 + legW) * s * f, hip.y);
  ctx.fill();

  // Right back
  ctx.beginPath();
  ctx.moveTo(hip.x + 3.5 * s * f, hip.y - 0.5 * s);
  ctx.lineTo(hip.x + (3 * s + brStep) * f, floorY);
  ctx.lineTo(hip.x + ((3 + legW) * s + brStep) * f, floorY);
  ctx.lineTo(hip.x + 5 * s * f, hip.y - 0.5 * s);
  ctx.fill();

  ctx.globalAlpha = 1;
}

// ─── Tail ───────────────────────────────────────────────────────────────────

function drawTail(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  pose: CreaturePose,
  s: number, f: number, t: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  if (bs.tailSegments <= 0) return;

  const tailBaseX = hip.x + 5 * s * f;
  const tailBaseY = hip.y + 1 * s;
  const wag = Math.sin(t * 3.5) * pose.tailWag * 8 * s;
  const curl = pose.tailCurl;

  ctx.strokeStyle = pal.body;
  ctx.lineWidth = 2 * s;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(tailBaseX, tailBaseY);

  // Multi-segment bezier tail
  const segLen = bs.tailLength / bs.tailSegments;
  let cx = tailBaseX;
  let cy = tailBaseY;

  for (let i = 0; i < bs.tailSegments; i++) {
    const frac = (i + 1) / bs.tailSegments;
    const segWag = wag * frac;
    const segCurl = curl * frac * 1.5;
    const cpx = cx + segLen * s * f * 0.6;
    const cpy = cy - (segLen * 0.8 + segCurl * 2) * s + segWag * 0.3;
    const endX = cx + segLen * s * f * (1 - frac * 0.3);
    const endY = cy - (segLen + segCurl * 3) * s + segWag;
    ctx.quadraticCurveTo(cpx, cpy, endX, endY);
    cx = endX;
    cy = endY;
  }

  ctx.stroke();

  // Tail tip glow for fiery creatures (phoenix, fire dragon)
  if (bs.tailSegments >= 4 && bs.tailLength > 20) {
    ctx.fillStyle = pal.accent;
    ctx.globalAlpha = 0.5 + 0.2 * Math.sin(t * 5);
    ctx.beginPath();
    ctx.arc(cx, cy, 2.5 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = lighten(pal.accent, 0.4);
    ctx.globalAlpha = 0.3 + 0.15 * Math.sin(t * 7);
    ctx.beginPath();
    ctx.arc(cx, cy, 1.5 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.lineCap = "butt";
}

// ─── Scratch Leg ────────────────────────────────────────────────────────────

function drawScratchLeg(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number, f: number, t: number,
  pal: CreaturePalette
): void {
  const scratchCycle = Math.sin(t * 18) * pose.scratchLeg;
  ctx.strokeStyle = pal.body;
  ctx.lineWidth = 1.5 * s;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(hip.x + 3 * s * f, hip.y);
  ctx.quadraticCurveTo(
    hip.x - 2 * s * f, hip.y - 6 * s + scratchCycle * 2 * s,
    head.x + 2 * s * f, head.y + headR * 0.6 + scratchCycle * 1.5 * s
  );
  ctx.stroke();

  // Paw at scratch point
  ctx.fillStyle = pal.body;
  ctx.beginPath();
  ctx.arc(
    head.x + 2 * s * f,
    head.y + headR * 0.6 + scratchCycle * 1.5 * s,
    1 * s, 0, Math.PI * 2
  );
  ctx.fill();
  ctx.lineCap = "butt";
}

// ─── Body ───────────────────────────────────────────────────────────────────

function drawBody(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  shoulder: { x: number; y: number },
  spineLen: number,
  spineAng: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  const bodyCx = (hip.x + shoulder.x) / 2;
  const bodyCy = (hip.y + shoulder.y) / 2;
  const bodyLen = spineLen * 0.65;
  const bodyWid = (bs.bodyWidth - pose.spineAngle * 0.8) * s;

  ctx.fillStyle = pal.body;

  // Main body ellipse
  ctx.beginPath();
  ctx.ellipse(bodyCx, bodyCy, bodyLen, bodyWid, spineAng * f, 0, Math.PI * 2);
  ctx.fill();

  // Haunches (rounded rump at hip)
  ctx.beginPath();
  ctx.ellipse(
    hip.x + 1 * s * f, hip.y - 0.5 * s,
    4 * s, 3.5 * s,
    (spineAng + 0.3) * f, 0, Math.PI * 2
  );
  ctx.fill();

  // Chest (rounded front at shoulder)
  ctx.beginPath();
  ctx.ellipse(
    shoulder.x, shoulder.y,
    3.5 * s, bodyWid * 0.9,
    (spineAng - 0.2) * f, 0, Math.PI * 2
  );
  ctx.fill();

  // Belly highlight (optional — adds depth)
  if (pose.spineAngle > 0.2) {
    ctx.fillStyle = pal.belly;
    ctx.globalAlpha = 0.15;
    ctx.beginPath();
    ctx.ellipse(bodyCx, bodyCy + bodyWid * 0.3, bodyLen * 0.5, bodyWid * 0.4, spineAng * f, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
}

// ─── Front Legs ─────────────────────────────────────────────────────────────

function drawFrontLegs(
  ctx: CanvasRenderingContext2D,
  shoulder: { x: number; y: number },
  floorY: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette,
  isWalking: boolean
): void {
  if (pose.spineAngle <= 0.12) return;

  const vis = Math.min(1, (pose.spineAngle - 0.12) / 0.25);
  ctx.globalAlpha = vis;
  ctx.fillStyle = pal.body;

  const legW = bs.legThickness * s;
  const flStep = isWalking ? Math.sin(pose.walkPhase) * 2 * s : 0;
  const frStep = isWalking ? Math.sin(pose.walkPhase + Math.PI) * 2 * s : 0;

  // Left front
  ctx.beginPath();
  ctx.moveTo(shoulder.x - 1.5 * s * f, shoulder.y + 1 * s);
  ctx.lineTo(shoulder.x + (-2 * s + flStep) * f, floorY);
  ctx.lineTo(shoulder.x + (-2 * s + legW + flStep) * f, floorY);
  ctx.lineTo(shoulder.x + 0 * f, shoulder.y + 1 * s);
  ctx.fill();

  // Right front
  ctx.beginPath();
  ctx.moveTo(shoulder.x + 0.5 * s * f, shoulder.y + 0.5 * s);
  ctx.lineTo(shoulder.x + (0 * s + frStep) * f, floorY);
  ctx.lineTo(shoulder.x + (legW + frStep) * f, floorY);
  ctx.lineTo(shoulder.x + 2 * s * f, shoulder.y + 0.5 * s);
  ctx.fill();

  // Paws
  ctx.fillStyle = pal.accent;
  for (const px of [
    shoulder.x + (-1.2 * s + flStep) * f,
    shoulder.x + (0.8 * s + frStep) * f,
  ]) {
    ctx.beginPath();
    ctx.ellipse(px, floorY, 1.3 * s, 0.6 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = 1;
}

// ─── Head ───────────────────────────────────────────────────────────────────

function drawHead(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  neckTop: { x: number; y: number },
  pose: CreaturePose,
  s: number, f: number, t: number,
  bs: BodyShape, pal: CreaturePalette,
  quality: number,
  eyeOffX: number = 0,
  eyeOffY: number = 0,
  blinkPhase: number = 0
): void {
  // Head circle
  ctx.fillStyle = pal.body;
  ctx.beginPath();
  ctx.arc(head.x, head.y, headR, 0, Math.PI * 2);
  ctx.fill();

  // Snout extension (for dogs, horses, wolves)
  if (bs.snoutLength > 0.3) {
    const snoutLen = bs.snoutLength * headR;
    const snoutW = headR * 0.6;
    ctx.beginPath();
    ctx.ellipse(
      head.x + snoutLen * 0.5 * f, head.y + headR * 0.2,
      snoutLen, snoutW,
      0, 0, Math.PI * 2
    );
    ctx.fill();
  }

  // Ears
  drawEars(ctx, head, headR, pose, s, f, t, bs, pal);

  // Eyes — smooth multi-phase blink with asymmetric close/open
  // Phase 0-0.35: closing (fast), 0.35-0.55: held shut, 0.55-1.0: opening (slow)
  if (pose.eyeOpen > 0.05) {
    let eyeScale = 1.0;
    if (blinkPhase > 0) {
      if (blinkPhase < 0.35) {
        // Closing — fast ease-in
        eyeScale = 1.0 - (blinkPhase / 0.35);
      } else if (blinkPhase < 0.55) {
        // Held shut
        eyeScale = 0;
      } else {
        // Opening — slow ease-out
        eyeScale = (blinkPhase - 0.55) / 0.45;
      }
      eyeScale = clampC(eyeScale, 0, 1);
    }

    if (eyeScale > 0.05) {
      // Draw scaled eyes — during blink, eyes narrow vertically
      drawEyesWithScale(ctx, head, headR, pose, s, f, bs, pal, eyeOffX, eyeOffY, eyeScale);
    } else {
      // Fully closed — thin line lids
      const blinkEyeY = head.y - 0.3 * s;
      ctx.strokeStyle = darken(pal.body, 0.15);
      ctx.lineWidth = 0.7 * s;
      ctx.beginPath();
      ctx.moveTo(head.x - 1.3 * s * f - 0.5 * s, blinkEyeY);
      ctx.lineTo(head.x - 1.3 * s * f + 0.5 * s, blinkEyeY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(head.x + 1.5 * s * f - 0.5 * s, blinkEyeY - 0.2 * s);
      ctx.lineTo(head.x + 1.5 * s * f + 0.5 * s, blinkEyeY - 0.2 * s);
      ctx.stroke();
    }
  }

  // Nose + whiskers (visible when head is lifted)
  if (pose.headLift > 0.15 && quality > 0.5) {
    drawNoseWhiskers(ctx, head, headR, pose, s, f, bs, pal);
  }

  // Yawn mouth
  if (pose.jawOpen > 0.1) {
    drawMouth(ctx, head, headR, pose, s, pal);
  }
}

// ─── Ears ───────────────────────────────────────────────────────────────────

function drawEars(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number, f: number, t: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  const earH = 3.5 * s * bs.earSize;
  const earW = 2.2 * s * bs.earSize;

  if (bs.earStyle === "pointed" || bs.earStyle === "tufted") {
    // Pointed/tufted ears (cats, wolves)
    // Left ear
    const elx = head.x - headR * 0.55 * f;
    const ely = head.y - headR * 0.5;
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.moveTo(elx - earW * 0.4 * f, ely);
    ctx.lineTo(elx + pose.earTwitchL * earW * 0.5 * f, ely - earH);
    ctx.lineTo(elx + earW * 0.5 * f, ely);
    ctx.fill();
    // Inner ear
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.moveTo(elx - earW * 0.2 * f, ely - 0.5 * s);
    ctx.lineTo(elx + pose.earTwitchL * earW * 0.5 * f, ely - earH + 0.7 * s);
    ctx.lineTo(elx + earW * 0.3 * f, ely - 0.5 * s);
    ctx.fill();

    // Right ear
    const erx = head.x + headR * 0.45 * f;
    const ery = head.y - headR * 0.6;
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.moveTo(erx - earW * 0.3 * f, ery);
    ctx.lineTo(erx + pose.earTwitchR * earW * 0.5 * f, ery - earH);
    ctx.lineTo(erx + earW * 0.5 * f, ery);
    ctx.fill();
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.moveTo(erx - earW * 0.1 * f, ery - 0.5 * s);
    ctx.lineTo(erx + pose.earTwitchR * earW * 0.5 * f, ery - earH + 0.7 * s);
    ctx.lineTo(erx + earW * 0.35 * f, ery - 0.5 * s);
    ctx.fill();
  } else if (bs.earStyle === "floppy") {
    // Floppy ears (dogs, some horses)
    const elx = head.x - headR * 0.6 * f;
    const ely = head.y - headR * 0.3;
    ctx.fillStyle = pal.markings;
    ctx.beginPath();
    ctx.ellipse(
      elx - earW * 0.3 * f,
      ely + earH * 0.4 + pose.earTwitchL * earH * 0.2,
      earW * 0.5, earH * 0.6,
      0.3 * f, 0, Math.PI * 2
    );
    ctx.fill();

    const erx = head.x + headR * 0.5 * f;
    const ery = head.y - headR * 0.4;
    ctx.fillStyle = pal.markings;
    ctx.beginPath();
    ctx.ellipse(
      erx + earW * 0.2 * f,
      ery + earH * 0.3 + pose.earTwitchR * earH * 0.2,
      earW * 0.5, earH * 0.55,
      -0.2 * f, 0, Math.PI * 2
    );
    ctx.fill();
  } else if (bs.earStyle === "round") {
    // Round ears (bears, mice)
    const elx = head.x - headR * 0.5 * f;
    const ely = head.y - headR * 0.7;
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.arc(elx, ely, earW * 0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.arc(elx, ely, earW * 0.3, 0, Math.PI * 2);
    ctx.fill();

    const erx = head.x + headR * 0.4 * f;
    const ery = head.y - headR * 0.75;
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.arc(erx, ery, earW * 0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.arc(erx, ery, earW * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ─── Eyes ───────────────────────────────────────────────────────────────────

function drawEyes(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette,
  eyeOffX: number = 0,
  eyeOffY: number = 0,
): void {
  const eyeR = 0.6 * s * pose.eyeOpen;
  const eyeY = head.y - 0.3 * s + eyeOffY;

  // Iris — offset by gaze direction toward player
  ctx.fillStyle = pal.eyes;
  ctx.beginPath();
  ctx.ellipse(head.x - 1.3 * s * f + eyeOffX, eyeY, eyeR, eyeR * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(head.x + 1.5 * s * f + eyeOffX, eyeY - 0.2 * s, eyeR, eyeR * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();

  // Pupil — slit for cats, round for dogs/horses
  ctx.fillStyle = "#0a0a0a";
  const pupilW = bs.snoutLength > 0.3 ? eyeR * 0.4 : eyeR * 0.25; // round vs slit
  ctx.beginPath();
  ctx.ellipse(head.x - 1.3 * s * f + eyeOffX, eyeY, pupilW, eyeR * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(head.x + 1.5 * s * f + eyeOffX, eyeY - 0.2 * s, pupilW, eyeR * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();

  // Eye specular highlight — tiny white dot that makes eyes feel alive
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  const specR = eyeR * 0.25;
  ctx.beginPath();
  ctx.arc(head.x - 1.3 * s * f + eyeOffX + specR * 0.5, eyeY - specR * 0.5, specR, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(head.x + 1.5 * s * f + eyeOffX + specR * 0.5, eyeY - 0.2 * s - specR * 0.5, specR, 0, Math.PI * 2);
  ctx.fill();
}

// ─── Eyes with Blink Scale ──────────────────────────────────────────────────
// During blink transitions, the eye narrows vertically while maintaining
// horizontal width. This creates a natural eyelid closing effect.

function drawEyesWithScale(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette,
  eyeOffX: number,
  eyeOffY: number,
  eyeScale: number,
): void {
  const fullR = 0.6 * s * pose.eyeOpen;
  const eyeR = fullR;  // horizontal stays constant
  const eyeRY = fullR * 0.7 * eyeScale;  // vertical narrows during blink
  const eyeY = head.y - 0.3 * s + eyeOffY;

  // Iris
  ctx.fillStyle = pal.eyes;
  ctx.beginPath();
  ctx.ellipse(head.x - 1.3 * s * f + eyeOffX, eyeY, eyeR, eyeRY, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(head.x + 1.5 * s * f + eyeOffX, eyeY - 0.2 * s, eyeR, eyeRY, 0, 0, Math.PI * 2);
  ctx.fill();

  // Pupil — shrinks with eye scale
  ctx.fillStyle = "#0a0a0a";
  const pupilW = bs.snoutLength > 0.3 ? eyeR * 0.4 : eyeR * 0.25;
  const pupilH = fullR * 0.6 * eyeScale;
  ctx.beginPath();
  ctx.ellipse(head.x - 1.3 * s * f + eyeOffX, eyeY, pupilW, pupilH, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(head.x + 1.5 * s * f + eyeOffX, eyeY - 0.2 * s, pupilW, pupilH, 0, 0, Math.PI * 2);
  ctx.fill();

  // Specular highlight — fades during blink
  if (eyeScale > 0.3) {
    ctx.fillStyle = `rgba(255,255,255,${0.6 * eyeScale})`;
    const specR = eyeR * 0.25;
    ctx.beginPath();
    ctx.arc(head.x - 1.3 * s * f + eyeOffX + specR * 0.5, eyeY - specR * 0.5, specR, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(head.x + 1.5 * s * f + eyeOffX + specR * 0.5, eyeY - 0.2 * s - specR * 0.5, specR, 0, Math.PI * 2);
    ctx.fill();
  }

  // Eyelid line — visible as eyes narrow, fades when fully open
  if (eyeScale < 0.8) {
    const lidAlpha = (1 - eyeScale) * 0.4;
    ctx.strokeStyle = `rgba(0,0,0,${lidAlpha})`;
    ctx.lineWidth = 0.4 * s;
    // Upper lid line
    ctx.beginPath();
    ctx.moveTo(head.x - 1.3 * s * f + eyeOffX - eyeR, eyeY - eyeRY * 0.3);
    ctx.lineTo(head.x - 1.3 * s * f + eyeOffX + eyeR, eyeY - eyeRY * 0.3);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(head.x + 1.5 * s * f + eyeOffX - eyeR, eyeY - 0.2 * s - eyeRY * 0.3);
    ctx.lineTo(head.x + 1.5 * s * f + eyeOffX + eyeR, eyeY - 0.2 * s - eyeRY * 0.3);
    ctx.stroke();
  }
}

// ─── Nose + Whiskers ────────────────────────────────────────────────────────

function drawNoseWhiskers(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  const snoutOffset = bs.snoutLength * headR * 0.5 * f;
  const noseY = head.y + headR * 0.55;
  const noseX = head.x + snoutOffset;

  // Nose
  ctx.fillStyle = pal.nose;
  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  if (bs.snoutLength > 0.3) {
    // Larger nose for dogs/horses
    ctx.ellipse(noseX, noseY, 1 * s, 0.7 * s, 0, 0, Math.PI * 2);
  } else {
    // Small triangle nose for cats
    ctx.moveTo(noseX, noseY - 0.6 * s);
    ctx.lineTo(noseX + 0.6 * s * f, noseY + 0.3 * s);
    ctx.lineTo(noseX - 0.6 * s * f, noseY + 0.3 * s);
  }
  ctx.fill();
  ctx.globalAlpha = 1;

  // Whiskers (cats and some dogs)
  if (bs.snoutLength < 0.5) {
    ctx.strokeStyle = "rgba(200,200,210,0.2)";
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.moveTo(noseX - 0.8 * s * f, noseY);
    ctx.lineTo(noseX - 4 * s * f, noseY - 1.2 * s);
    ctx.moveTo(noseX - 0.8 * s * f, noseY + 0.3 * s);
    ctx.lineTo(noseX - 3.8 * s * f, noseY + 0.8 * s);
    ctx.moveTo(noseX + 0.8 * s * f, noseY - 0.2 * s);
    ctx.lineTo(noseX + 4 * s * f, noseY - 1.5 * s);
    ctx.moveTo(noseX + 0.8 * s * f, noseY + 0.2 * s);
    ctx.lineTo(noseX + 3.8 * s * f, noseY + 0.6 * s);
    ctx.stroke();
  }
}

// ─── Mouth ──────────────────────────────────────────────────────────────────

function drawMouth(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  pose: CreaturePose,
  s: number,
  pal: CreaturePalette
): void {
  const mouthY = head.y + headR * 0.5;
  const mouthH = pose.jawOpen * 2 * s;

  ctx.fillStyle = "#3a1818";
  ctx.beginPath();
  ctx.ellipse(head.x, mouthY, 1.3 * s, mouthH, 0, 0, Math.PI * 2);
  ctx.fill();

  // Tongue
  if (pose.jawOpen > 0.4) {
    ctx.fillStyle = "#c06060";
    ctx.beginPath();
    ctx.ellipse(head.x, mouthY + mouthH * 0.3, 0.6 * s, mouthH * 0.35, 0, 0, Math.PI);
    ctx.fill();
  }
}

// ─── Mane ───────────────────────────────────────────────────────────────────

function drawMane(
  ctx: CanvasRenderingContext2D,
  shoulder: { x: number; y: number },
  neckTop: { x: number; y: number },
  head: { x: number; y: number },
  spineAng: number,
  s: number, f: number,
  pal: CreaturePalette
): void {
  ctx.fillStyle = pal.markings;
  ctx.globalAlpha = 0.7;

  // Mane flows from head down the neck
  const manePoints = 8;
  ctx.beginPath();
  for (let i = 0; i <= manePoints; i++) {
    const t = i / manePoints;
    const baseX = head.x * (1 - t) + shoulder.x * t;
    const baseY = head.y * (1 - t) + shoulder.y * t;
    // Mane offset perpendicular to neck direction
    const perpX = -Math.sin(spineAng) * (2 + Math.sin(t * Math.PI) * 3) * s;
    const perpY = Math.cos(spineAng) * (2 + Math.sin(t * Math.PI) * 3) * s;

    if (i === 0) {
      ctx.moveTo(baseX + perpX * f, baseY + perpY);
    } else {
      ctx.lineTo(baseX + perpX * f, baseY + perpY);
    }
  }
  // Return along the neck line
  for (let i = manePoints; i >= 0; i--) {
    const t = i / manePoints;
    const baseX = head.x * (1 - t) + shoulder.x * t;
    const baseY = head.y * (1 - t) + shoulder.y * t;
    ctx.lineTo(baseX, baseY);
  }
  ctx.fill();
  ctx.globalAlpha = 1;
}

// ─── Fire Glow ──────────────────────────────────────────────────────────────

function drawFireGlow(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  shoulder: { x: number; y: number },
  spineLen: number,
  spineAng: number,
  pose: CreaturePose,
  s: number, f: number, t: number,
  bs: BodyShape,
  ambientGlow?: string
): void {
  if (!ambientGlow) return;

  const glow = 0.12 + 0.06 * Math.sin(t * 3.5);
  ctx.globalAlpha = glow;
  ctx.fillStyle = ambientGlow;

  const bodyCx = (hip.x + shoulder.x) / 2;
  const bodyCy = (hip.y + shoulder.y) / 2;
  const bodyLen = spineLen * 0.65;
  const bodyWid = (bs.bodyWidth - pose.spineAngle * 0.8) * s;

  ctx.beginPath();
  ctx.ellipse(
    bodyCx - 3 * s * f, bodyCy - 1 * s,
    bodyLen * 0.6, bodyWid * 0.8,
    spineAng * f, 0, Math.PI * 2
  );
  ctx.fill();
  ctx.globalAlpha = 1;
}

// ─── Ground Shadow ─────────────────────────────────────────────────────────

function drawGroundShadow(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  floorY: number,
  spineLen: number,
  s: number, f: number,
  pose: CreaturePose
): void {
  const shadowW = spineLen * 0.7 + 3 * s;
  const shadowH = 2 * s;
  const shadowX = hip.x + spineLen * 0.15 * f;

  ctx.fillStyle = "rgba(0,0,0,0.12)";
  ctx.beginPath();
  ctx.ellipse(shadowX, floorY + 1 * s, shadowW, shadowH, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ─── Wings ─────────────────────────────────────────────────────────────────

/**
 * Procedural membrane wing. side: -1 = back wing (behind body), 1 = front wing.
 * Wings fold and extend with breathing, flap when walking fast.
 */
function drawWing(
  ctx: CanvasRenderingContext2D,
  shoulder: { x: number; y: number },
  s: number, f: number, t: number,
  bs: BodyShape, pal: CreaturePalette,
  side: number,
  quality: number
): void {
  if (!bs.wingSpan) return;

  const wingLen = bs.wingSpan * s * 0.5;
  const flapPhase = Math.sin(t * 2.2) * 0.3;
  const fold = 0.35 + flapPhase; // 0=folded, 1=extended

  // Wing root offset — back wing is further from viewer
  const depthOffset = side === -1 ? -2 * s : 1 * s;
  const rootX = shoulder.x + depthOffset * f;
  const rootY = shoulder.y - 3 * s;

  // Wing angles
  const upperAng = -1.2 + fold * 0.5;
  const lowerAng = upperAng - 0.3 - fold * 0.4;

  // Upper wing (root to mid-joint)
  const midX = rootX + Math.cos(upperAng) * wingLen * 0.6 * f * side;
  const midY = rootY + Math.sin(upperAng) * wingLen * 0.6;

  // Wing tip
  const tipX = midX + Math.cos(lowerAng) * wingLen * 0.5 * f * side;
  const tipY = midY + Math.sin(lowerAng) * wingLen * 0.5;

  // Membrane trailing edge
  const trailX = rootX + wingLen * 0.1 * f * side;
  const trailY = rootY + wingLen * 0.3;

  // Draw wing membrane
  ctx.globalAlpha = side === -1 ? 0.35 : 0.55;
  ctx.fillStyle = darken(pal.body, 0.05);
  ctx.beginPath();
  ctx.moveTo(rootX, rootY);
  ctx.quadraticCurveTo(
    (rootX + midX) / 2 + 2 * s * f * side,
    (rootY + midY) / 2 - 3 * s,
    midX, midY
  );
  ctx.quadraticCurveTo(
    (midX + tipX) / 2 + 1 * s * f * side,
    (midY + tipY) / 2 - 1 * s,
    tipX, tipY
  );
  ctx.lineTo(trailX, trailY);
  ctx.closePath();
  ctx.fill();

  // Wing bone structure
  ctx.strokeStyle = pal.markings;
  ctx.lineWidth = 1.2 * s;
  ctx.globalAlpha = side === -1 ? 0.3 : 0.5;
  ctx.beginPath();
  ctx.moveTo(rootX, rootY);
  ctx.lineTo(midX, midY);
  ctx.lineTo(tipX, tipY);
  ctx.stroke();

  // Membrane veins (at high quality)
  if (quality > 0.7) {
    ctx.strokeStyle = darken(pal.body, 0.15);
    ctx.lineWidth = 0.4 * s;
    ctx.globalAlpha = side === -1 ? 0.15 : 0.25;
    const veins = 3;
    for (let v = 1; v <= veins; v++) {
      const vt = v / (veins + 1);
      const vStartX = rootX + (midX - rootX) * vt;
      const vStartY = rootY + (midY - rootY) * vt;
      const vEndX = rootX + (trailX - rootX) * vt;
      const vEndY = rootY + (trailY - rootY) * vt * 1.2;
      ctx.beginPath();
      ctx.moveTo(vStartX, vStartY);
      ctx.lineTo(vEndX, vEndY);
      ctx.stroke();
    }
  }

  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;
}

// ─── Body Markings ─────────────────────────────────────────────────────────

/**
 * Subtle stripe/spot patterns using the markings palette color.
 * Adds visual variety between individuals and breeds.
 */
function drawMarkings(
  ctx: CanvasRenderingContext2D,
  hip: { x: number; y: number },
  shoulder: { x: number; y: number },
  spineLen: number,
  spineAng: number,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  // Only draw if markings differ from body color
  if (pal.markings === pal.body) return;

  const bodyCx = (hip.x + shoulder.x) / 2;
  const bodyCy = (hip.y + shoulder.y) / 2;
  const bodyLen = spineLen * 0.65;

  ctx.fillStyle = pal.markings;
  ctx.globalAlpha = 0.2;

  // Stripe pattern along spine — 3-5 subtle stripes
  const stripeCount = Math.max(3, Math.min(5, Math.floor(bs.bodyLength / 5)));
  for (let i = 0; i < stripeCount; i++) {
    const t = (i + 0.5) / stripeCount;
    const sx = hip.x + (shoulder.x - hip.x) * t;
    const sy = hip.y + (shoulder.y - hip.y) * t;
    const stripeW = 1.5 * s;
    const stripeH = (bs.bodyWidth * 0.5 + Math.sin(t * Math.PI) * 1.5) * s;

    ctx.beginPath();
    ctx.ellipse(sx, sy, stripeW, stripeH, spineAng * f + 0.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Dorsal stripe (subtle darker line along the back)
  ctx.strokeStyle = pal.markings;
  ctx.lineWidth = 0.8 * s;
  ctx.globalAlpha = 0.12;
  ctx.beginPath();
  ctx.moveTo(hip.x + 2 * s * f, hip.y - 1 * s);
  ctx.quadraticCurveTo(
    bodyCx, bodyCy - bs.bodyWidth * s * 0.6,
    shoulder.x - 1 * s * f, shoulder.y - 1 * s
  );
  ctx.stroke();

  ctx.globalAlpha = 1;
}

// ─── Horns ─────────────────────────────────────────────────────────────────

/**
 * Draw horns for dragons and similar creatures.
 * Only drawn if snoutLength > 0.8 (dragon/monster heads).
 */
function drawHorns(
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  headR: number,
  s: number, f: number,
  bs: BodyShape, pal: CreaturePalette
): void {
  if (bs.snoutLength < 0.8) return;

  const hornLen = headR * 1.2;
  const hornW = 1.2 * s;

  ctx.strokeStyle = darken(pal.body, 0.3);
  ctx.lineWidth = hornW;
  ctx.lineCap = "round";

  // Left horn
  const lhx = head.x - headR * 0.35 * f;
  const lhy = head.y - headR * 0.6;
  ctx.beginPath();
  ctx.moveTo(lhx, lhy);
  ctx.quadraticCurveTo(
    lhx - hornLen * 0.2 * f, lhy - hornLen * 0.8,
    lhx - hornLen * 0.1 * f, lhy - hornLen
  );
  ctx.stroke();

  // Right horn
  const rhx = head.x + headR * 0.25 * f;
  const rhy = head.y - headR * 0.7;
  ctx.beginPath();
  ctx.moveTo(rhx, rhy);
  ctx.quadraticCurveTo(
    rhx + hornLen * 0.15 * f, rhy - hornLen * 0.8,
    rhx + hornLen * 0.05 * f, rhy - hornLen
  );
  ctx.stroke();

  ctx.lineCap = "butt";
}

// ─── Color Utilities ────────────────────────────────────────────────────────

/** Darken a hex color by a fraction (0 = no change, 1 = black) */
function darken(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = 1 - amount;
  return `#${Math.round(r * f).toString(16).padStart(2, "0")}${Math.round(g * f).toString(16).padStart(2, "0")}${Math.round(b * f).toString(16).padStart(2, "0")}`;
}

/** Lighten a hex color by blending toward white */
function lighten(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `#${Math.round(r + (255 - r) * amount).toString(16).padStart(2, "0")}${Math.round(g + (255 - g) * amount).toString(16).padStart(2, "0")}${Math.round(b + (255 - b) * amount).toString(16).padStart(2, "0")}`;
}
